import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import TransactionDetailModal from './modals/TransactionDetailModal';
import BookingDetailModal from './modals/BookingDetailModal';
import { useAdminData } from './AdminContext';
import { 
  DollarSign, 
  TrendingUp, 
  Percent, 
  CreditCard, 
  Building2, 
  Clock, 
  RefreshCw,
  Loader,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Tiny diagnostic bucket cell for the Payment Health tile.
const Bucket = ({ label, value, tone = 'gray', testId }) => {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    blue:    'bg-blue-50 text-blue-800 border-blue-200',
    amber:   'bg-amber-50 text-amber-900 border-amber-200',
    red:     'bg-red-50 text-red-800 border-red-200',
    gray:    'bg-gray-50 text-gray-700 border-gray-200',
  };
  return (
    <div className={`rounded-md border px-2 py-1.5 ${tones[tone] || tones.gray}`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-base font-semibold leading-tight">{value}</p>
    </div>
  );
};

const AdminPaymentsTab = () => {
  const { transactions, bookings } = useAdminData();
  const [selectedTransactionDetail, setSelectedTransactionDetail] = useState(null);
  const [selectedBookingDetail, setSelectedBookingDetail] = useState(null);
  
  // Earnings summary state
  const [earningsSummary, setEarningsSummary] = useState(null);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [earningsPeriod, setEarningsPeriod] = useState('all');

  // Phase 1 payment health (read-only diagnostic)
  const [paymentHealth, setPaymentHealth] = useState(null);
  const [paymentHealthLoading, setPaymentHealthLoading] = useState(false);
  const fetchPaymentHealth = async () => {
    setPaymentHealthLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/api/admin/payment-health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPaymentHealth(r.data);
    } catch (e) {
      console.error('Failed to fetch payment health:', e);
    } finally {
      setPaymentHealthLoading(false);
    }
  };
  useEffect(() => { fetchPaymentHealth(); /* eslint-disable-next-line */ }, []);

  // Fetch earnings summary
  const fetchEarningsSummary = async (period = earningsPeriod) => {
    setEarningsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/earnings-summary?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEarningsSummary(response.data);
    } catch (error) {
      console.error('Error fetching earnings summary:', error);
    } finally {
      setEarningsLoading(false);
    }
  };

  useEffect(() => {
    fetchEarningsSummary();
  }, []);

  const handlePeriodChange = (newPeriod) => {
    setEarningsPeriod(newPeriod);
    fetchEarningsSummary(newPeriod);
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'succeeded') return 'bg-green-100 text-green-800';
    if (status === 'pending') return 'bg-yellow-100 text-yellow-800';
    if (status === 'failed') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getStatusDisplay = (status) => {
    if (status === 'succeeded') return 'Completed';
    if (status === 'pending') return 'Processing';
    if (status === 'failed') return 'Failed';
    return 'Processing';
  };

  return (
    <div className="space-y-6">
      {/* ── Payment Health (Phase 1 — read-only diagnostic) ──
          Operational visibility tile. Aggregation-only over bookings.
          NO mutations. See /api/admin/payment-health. */}
      <Card data-testid="payment-health-tile" className="border-amber-200 bg-amber-50/40">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4 text-amber-700" />
                Payment Health
              </CardTitle>
              <CardDescription className="text-xs">
                Read-only diagnostic. Bucket counts across the bookings collection. Phase 0 containment: <code>process_auto_releases()</code> is DISABLED.
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchPaymentHealth}
              disabled={paymentHealthLoading}
              data-testid="payment-health-refresh"
            >
              <RefreshCw className={`h-4 w-4 ${paymentHealthLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!paymentHealth ? (
            <p className="text-xs text-gray-500">{paymentHealthLoading ? 'Loading…' : 'No data'}</p>
          ) : (
            <div className="space-y-3">
              {/* Top-line buckets — dense, single row */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-xs">
                <Bucket label="Escrow held" value={paymentHealth.buckets.escrow_held} tone="emerald" testId="bucket-escrow-held" />
                <Bucket label="Transferred" value={paymentHealth.buckets.escrow_transferred} tone="blue" testId="bucket-escrow-transferred" />
                <Bucket label="Awaiting final" value={paymentHealth.buckets.awaiting_final_payment} tone={paymentHealth.buckets.awaiting_final_payment > 0 ? 'amber' : 'gray'} testId="bucket-awaiting-final" />
                <Bucket label="Disputed" value={paymentHealth.buckets.escrow_disputed} tone={paymentHealth.buckets.escrow_disputed > 0 ? 'red' : 'gray'} testId="bucket-disputed" />
                <Bucket label="Cancelled" value={paymentHealth.buckets.cancelled} tone="gray" testId="bucket-cancelled" />
                <Bucket label="Escrow active" value={paymentHealth.buckets.escrow_active} tone="emerald" testId="bucket-escrow-active" />
                <Bucket label="Legacy dest_charge" value={paymentHealth.buckets.destination_charge_legacy_active} tone={paymentHealth.buckets.destination_charge_legacy_active > 0 ? 'red' : 'gray'} testId="bucket-legacy-destination-charge" />
                <Bucket label="Missing model" value={paymentHealth.buckets.missing_payment_model} tone={paymentHealth.buckets.missing_payment_model > 0 ? 'amber' : 'gray'} testId="bucket-missing-payment-model" />
                <Bucket label="All bookings" value={paymentHealth.totals.all_bookings} tone="gray" testId="bucket-total" />
                <Bucket label="Last 90d" value={paymentHealth.totals.last_90d} tone="gray" testId="bucket-90d" />
              </div>

              {/* Inconsistent + stuck signals — only show if non-zero */}
              {(paymentHealth.inconsistent.deposit_paid_no_transfer_status > 0
                || paymentHealth.inconsistent.escrow_held_no_release_at > 0
                || paymentHealth.inconsistent.awaiting_final_no_auto_release_at > 0
                || paymentHealth.stuck.awaiting_final_past_deadline > 0) && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900 space-y-0.5" data-testid="payment-health-anomalies">
                  <p className="font-medium">Anomalies</p>
                  {paymentHealth.inconsistent.deposit_paid_no_transfer_status > 0 && (
                    <p>• deposit_paid but no transfer_status: <b>{paymentHealth.inconsistent.deposit_paid_no_transfer_status}</b> (legacy informational)</p>
                  )}
                  {paymentHealth.inconsistent.escrow_held_no_release_at > 0 && (
                    <p>• escrow held without release_at: <b>{paymentHealth.inconsistent.escrow_held_no_release_at}</b></p>
                  )}
                  {paymentHealth.inconsistent.awaiting_final_no_auto_release_at > 0 && (
                    <p>• awaiting_final without auto_release_at: <b>{paymentHealth.inconsistent.awaiting_final_no_auto_release_at}</b></p>
                  )}
                  {paymentHealth.stuck.awaiting_final_past_deadline > 0 && (
                    <p className="text-red-700">• awaiting_final past deadline (Phase 0 disabled): <b>{paymentHealth.stuck.awaiting_final_past_deadline}</b> — handle manually</p>
                  )}
                </div>
              )}

              {/* Incomplete-vendor / stranded-funds risk panel — only when non-zero.
                  Read-only. Each metric is a count from /api/admin/payment-health
                  → incomplete_vendor_risk. No mutations from this UI. */}
              {paymentHealth.incomplete_vendor_risk && (
                paymentHealth.incomplete_vendor_risk.transfer_failed > 0
                || paymentHealth.incomplete_vendor_risk.paid_no_vendor_stripe_on_booking > 0
                || paymentHealth.incomplete_vendor_risk.legacy_destination_charge_no_destination > 0
                || paymentHealth.incomplete_vendor_risk.escrow_fallback_count > 0
                || paymentHealth.incomplete_vendor_risk.incomplete_vendors_with_paid_bookings > 0
              ) && (
                <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-900 space-y-0.5" data-testid="payment-health-incomplete-vendor-risk">
                  <p className="font-medium">Incomplete-vendor / stranded-funds risk</p>
                  {paymentHealth.incomplete_vendor_risk.transfer_failed > 0 && (
                    <p data-testid="risk-transfer-failed">• transfer_failed bookings: <b>{paymentHealth.incomplete_vendor_risk.transfer_failed}</b></p>
                  )}
                  {paymentHealth.incomplete_vendor_risk.paid_no_vendor_stripe_on_booking > 0 && (
                    <p data-testid="risk-paid-no-vendor-stripe">• paid bookings with no vendor Stripe destination on booking: <b>{paymentHealth.incomplete_vendor_risk.paid_no_vendor_stripe_on_booking}</b></p>
                  )}
                  {paymentHealth.incomplete_vendor_risk.legacy_destination_charge_no_destination > 0 && (
                    <p data-testid="risk-legacy-destination-charge-no-destination">• legacy destination_charge w/ payment collected, no destination: <b>{paymentHealth.incomplete_vendor_risk.legacy_destination_charge_no_destination}</b></p>
                  )}
                  {paymentHealth.incomplete_vendor_risk.escrow_fallback_count > 0 && (
                    <p className="text-amber-800" data-testid="risk-escrow-fallback">• safety-fallback bookings (legacy→escrow, vendor missing Stripe): <b>{paymentHealth.incomplete_vendor_risk.escrow_fallback_count}</b></p>
                  )}
                  {paymentHealth.incomplete_vendor_risk.incomplete_vendors_with_paid_bookings > 0 && (
                    <p data-testid="risk-incomplete-vendors-with-paid"><b>{paymentHealth.incomplete_vendor_risk.incomplete_vendors_with_paid_bookings}</b> incomplete vendor{paymentHealth.incomplete_vendor_risk.incomplete_vendors_with_paid_bookings === 1 ? '' : 's'} with paid bookings</p>
                  )}
                </div>
              )}

              <p className="text-[10px] text-gray-500" data-testid="payment-health-timestamp">
                Generated {new Date(paymentHealth.generated_at).toLocaleString()}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Earnings Summary Card - Admin Only */}
      <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Platform Earnings Summary
              </CardTitle>
              <CardDescription>Revenue and fee breakdown (Admin Only)</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={earningsPeriod} onValueChange={handlePeriodChange}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="all">All Time</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => fetchEarningsSummary()}
                disabled={earningsLoading}
              >
                {earningsLoading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {earningsLoading && !earningsSummary ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : earningsSummary ? (
            <div className="space-y-6">
              {/* Main Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Gross Revenue */}
                <div className="bg-white rounded-xl p-4 border shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-gray-500 font-medium">Gross Revenue</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(earningsSummary.totals?.gross_revenue)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {earningsSummary.completed_bookings_count} completed bookings
                  </p>
                </div>

                {/* Platform Fees Collected */}
                <div className="bg-white rounded-xl p-4 border shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Percent className="w-5 h-5 text-blue-600" />
                    <span className="text-sm text-gray-500 font-medium">Platform Fees (5%)</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(earningsSummary.totals?.platform_fees_collected)}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    <ArrowUpRight className="w-3 h-3 text-green-500" />
                    <span className="text-xs text-green-600">Platform revenue</span>
                  </div>
                </div>

                {/* Estimated Stripe Fees */}
                <div className="bg-white rounded-xl p-4 border shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-5 h-5 text-purple-600" />
                    <span className="text-sm text-gray-500 font-medium">Est. Stripe Fees</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-600">
                    {formatCurrency(earningsSummary.totals?.estimated_stripe_fees)}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    <ArrowDownRight className="w-3 h-3 text-red-500" />
                    <span className="text-xs text-gray-500">~2.9% + $0.30/txn</span>
                  </div>
                </div>

                {/* Net Platform Revenue */}
                <div className="bg-white rounded-xl p-4 border border-green-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-gray-500 font-medium">Net Platform Revenue</span>
                  </div>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(earningsSummary.totals?.net_platform_revenue)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    After Stripe fees
                  </p>
                </div>
              </div>

              {/* Secondary Stats Row */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Vendor Payouts */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Net Vendor Payouts</span>
                    <span className="text-lg font-semibold text-gray-900">
                      {formatCurrency(earningsSummary.totals?.net_vendor_payouts)}
                    </span>
                  </div>
                </div>

                {/* Payment Breakdown */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Deposits Collected</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatCurrency(earningsSummary.payment_breakdown?.deposits_collected)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Final Payments</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatCurrency(earningsSummary.payment_breakdown?.final_payments_collected)}
                    </span>
                  </div>
                </div>

                {/* Pending Revenue */}
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">Pending Revenue</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-amber-700">{earningsSummary.pending?.booking_count} bookings</span>
                    <span className="text-lg font-semibold text-amber-800">
                      {formatCurrency(earningsSummary.pending?.expected_revenue)}
                    </span>
                  </div>
                  <p className="text-xs text-amber-600 mt-1">
                    Expected fees: {formatCurrency(earningsSummary.pending?.expected_platform_fees)}
                  </p>
                </div>
              </div>

              {/* Top Vendors Table */}
              {earningsSummary.vendor_breakdown && earningsSummary.vendor_breakdown.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Top Vendors by Revenue</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Vendor</TableHead>
                          <TableHead className="text-right">Bookings</TableHead>
                          <TableHead className="text-right">Gross Revenue</TableHead>
                          <TableHead className="text-right">Platform Fee</TableHead>
                          <TableHead className="text-right">Vendor Payout</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {earningsSummary.vendor_breakdown.slice(0, 10).map((vendor) => (
                          <TableRow key={vendor.vendor_id}>
                            <TableCell className="font-medium">{vendor.business_name}</TableCell>
                            <TableCell className="text-right">{vendor.booking_count}</TableCell>
                            <TableCell className="text-right">{formatCurrency(vendor.gross_revenue)}</TableCell>
                            <TableCell className="text-right text-blue-600">{formatCurrency(vendor.platform_fee)}</TableCell>
                            <TableCell className="text-right text-green-600">{formatCurrency(vendor.vendor_payout)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No earnings data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Transactions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Transactions</CardTitle>
          <CardDescription>View and manage payment transactions</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Payments Table */}
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Transaction ID</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      No transactions found
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((transaction) => {
                    const status = transaction.payment_status || transaction.status;
                    return (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          <div>
                            <p className="font-mono text-xs">{transaction.id?.slice(0, 12)}...</p>
                            <p className="text-xs text-gray-500">{transaction.type || 'payment'}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{transaction.customer_name || 'N/A'}</p>
                            <p className="text-sm text-gray-500">{transaction.customer_email || ''}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">Booking: {transaction.booking_id?.slice(0, 8)}...</p>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">${transaction.amount?.toFixed(2) || '0.00'}</p>
                          {transaction.platform_fee && (
                            <p className="text-xs text-blue-600">Fee: ${transaction.platform_fee?.toFixed(2)}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusBadgeClass(status)}>
                            {getStatusDisplay(status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{transaction.created_at ? new Date(transaction.created_at).toLocaleDateString() : 'N/A'}</p>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => setSelectedTransactionDetail(transaction)}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Transaction Details Modal */}
      <TransactionDetailModal 
        transaction={selectedTransactionDetail}
        bookings={bookings}
        onClose={() => setSelectedTransactionDetail(null)}
        onViewBooking={(booking) => setSelectedBookingDetail(booking)}
      />

      {/* Booking Details Modal (for viewing from transaction) */}
      <BookingDetailModal 
        booking={selectedBookingDetail} 
        onClose={() => setSelectedBookingDetail(null)} 
      />
    </div>
  );
};

export default AdminPaymentsTab;
